import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { Calculator, Receipt, Euro, FileSpreadsheet, AlertCircle, Loader2, PiggyBank } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ComptabiliteFactures from './comptabilite/ComptabiliteFactures';
import ComptabiliteRecettes from './comptabilite/ComptabiliteRecettes';
import ComptabiliteTableau from './comptabilite/ComptabiliteTableau';
import ComptabiliteBudget from './comptabilite/ComptabiliteBudget';

const Comptabilite = ({ user }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeSection, setActiveSection] = useState('factures');
  const [loading, setLoading] = useState(false);
  
  const coproId = user?.copropriete_active_id;
  
  const [postesDepenses, setPostesDepenses] = useState([]);
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [factures, setFactures] = useState([]);
  const [recettes, setRecettes] = useState([]);

  const canAccess = user && (user.role === 'president' || user.role === 'président' || user.role === 'tresorier' || user.role === 'trésorier' || user.email === 'gmpdeveloppement@gmail.com');

  const fetchData = useCallback(async () => {
    if (!coproId) return;
    setLoading(true);
    try {
      const [postesRes, coproRes, comptaRes] = await Promise.all([
        apiClient.get(`/coproprietes/${coproId}/postes`),
        apiClient.get(`/coproprietes/${coproId}/coproprietaires`),
        apiClient.get(`/coproprietes/${coproId}/comptabilite?annee=${selectedYear}`)
      ]);
      setPostesDepenses(postesRes.data || []);
      setCoproprietaires(coproRes.data || []);
      setFactures(comptaRes.data?.factures || []);
      setRecettes(comptaRes.data?.recettes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [coproId, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportExcel = async () => {
    try {
      const response = await apiClient.get(`/coproprietes/${coproId}/comptabilite/export?annee=${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comptabilite_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const totalDepenses = factures.reduce((sum, f) => sum + parseFloat(f.montant || 0), 0);
  const totalRecettes = recettes.reduce((sum, r) => sum + parseFloat(r.montant || 0), 0);
  const solde = totalRecettes - totalDepenses;

  if (!canAccess) {
    return (
      <div className="text-center py-20" data-testid="comptabilite-access-denied">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Accès restreint</h2>
        <p className="text-slate-600">Ce module est réservé au président, trésorier et syndic.</p>
      </div>
    );
  }

  const years = [2023, 2024, 2025, 2026, 2027];
  const tabs = [
    { id: 'factures', label: 'Factures', icon: Receipt },
    { id: 'recettes', label: 'Recettes', icon: Euro },
    { id: 'budget', label: 'Budget', icon: PiggyBank },
    { id: 'tableau', label: 'Tableau', icon: FileSpreadsheet }
  ];

  return (
    <div className="fade-in space-y-6" data-testid="comptabilite-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Calculator className="w-8 h-8 text-emerald-600" />
            Comptabilité
          </h1>
          <p className="text-slate-500 mt-1">Gestion des dépenses et recettes</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-slate-300 rounded-lg"
            data-testid="year-selector"
          >
            {years.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
          <Button onClick={handleExportExcel} variant="outline" className="gap-2" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4" /> Exporter Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-slate-600">Total Dépenses</span>
          </div>
          <p className="text-2xl font-bold text-red-600" data-testid="total-depenses">{totalDepenses.toFixed(2)} €</p>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-slate-600">Total Recettes</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600" data-testid="total-recettes">{totalRecettes.toFixed(2)} €</p>
        </div>
        <div className={`bg-white rounded-xl border p-6 ${solde >= 0 ? 'border-emerald-200' : 'border-red-200'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${solde >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <Calculator className={`w-5 h-5 ${solde >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <span className="text-slate-600">Solde</span>
          </div>
          <p className={`text-2xl font-bold ${solde >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="solde">
            {solde >= 0 ? '+' : ''}{solde.toFixed(2)} €
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map(tab => (
          <button 
            key={tab.id} 
            onClick={() => setActiveSection(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeSection === tab.id 
                ? 'border-emerald-600 text-emerald-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : (
        <>
          {activeSection === 'factures' && (
            <ComptabiliteFactures
              postesDepenses={postesDepenses}
              factures={factures}
              selectedYear={selectedYear}
              coproId={coproId}
              apiClient={apiClient}
              getErrorMessage={getErrorMessage}
              onRefresh={fetchData}
              loading={loading}
            />
          )}

          {activeSection === 'recettes' && (
            <ComptabiliteRecettes
              coproprietaires={coproprietaires}
              recettes={recettes}
              selectedYear={selectedYear}
              coproId={coproId}
              apiClient={apiClient}
              getErrorMessage={getErrorMessage}
              onRefresh={fetchData}
            />
          )}

          {activeSection === 'budget' && (
            <ComptabiliteBudget
              postesDepenses={postesDepenses}
              selectedYear={selectedYear}
              coproId={coproId}
              apiClient={apiClient}
              getErrorMessage={getErrorMessage}
              onRefresh={fetchData}
              coproprietaires={coproprietaires}
            />
          )}

          {activeSection === 'tableau' && (
            <ComptabiliteTableau
              postesDepenses={postesDepenses}
              coproprietaires={coproprietaires}
              factures={factures}
              recettes={recettes}
              selectedYear={selectedYear}
              coproId={coproId}
              apiClient={apiClient}
              getErrorMessage={getErrorMessage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Comptabilite;
